import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Plus, Pencil, Trash2, CalendarDays } from "lucide-react";

const fmtD = (iso) => (iso ? iso.split("-").reverse().join(".") : "");
const daysOf = (a, b) => Math.round((new Date(b + "T00:00:00Z") - new Date(a + "T00:00:00Z")) / 86400000) + 1;

const KIND = {
  vacation: { label: "Concediu", emoji: "🏖", cls: "bg-blue-50 text-blue-800 border-blue-200" },
  unavailable: { label: "Absență", emoji: "🚫", cls: "bg-amber-50 text-amber-800 border-amber-200" },
};
const STATUS = {
  approved: { label: "Aprobat", cls: "bg-emerald-100 text-emerald-800" },
  pending: { label: "În așteptare", cls: "bg-amber-100 text-amber-800" },
  rejected: { label: "Respins", cls: "bg-red-100 text-red-700" },
  returned: { label: "Revenit", cls: "bg-zinc-100 text-zinc-600" },
};

export const PersonVacationsTab = ({ personId, personName, canEdit }) => {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} (nou) | vacanță existentă

  const load = () =>
    api.get(`/vacations?person_id=${personId}&include_returned=true`)
      .then((r) => setRows((r.data || []).filter((v) => v.person_id === personId)))
      .catch(() => setRows([]));
  useEffect(() => { load(); }, [personId]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (v) => {
    if (!window.confirm(`Ștergi ${v.kind === "unavailable" ? "absența" : "concediul"} ${fmtD(v.start_date)} → ${fmtD(v.end_date)}?`)) return;
    try {
      await api.delete(`/vacations/${v.id}`);
      toast.success("Șters");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eroare la ștergere"); }
  };

  if (!rows) return <div className="text-sm text-zinc-500 py-6">Se încarcă…</div>;

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = rows.filter((v) => v.end_date >= today).sort((a, b) => a.start_date.localeCompare(b.start_date));
  const past = rows.filter((v) => v.end_date < today).sort((a, b) => b.start_date.localeCompare(a.start_date));

  const Row = ({ v }) => {
    const k = KIND[v.kind === "unavailable" ? "unavailable" : "vacation"];
    const s = STATUS[v.status] || STATUS.pending;
    return (
      <div className="bg-white border border-zinc-200 rounded-md px-3 py-2 flex flex-wrap items-center gap-x-3 gap-y-1" data-testid={`vacation-row-${v.id}`}>
        <span className={`text-[11px] font-bold px-2 py-0.5 rounded border ${k.cls}`}>{k.emoji} {k.label}</span>
        <span className="text-sm font-bold text-zinc-900 tabular-nums">{fmtD(v.start_date)} → {fmtD(v.end_date)}</span>
        <span className="text-xs text-zinc-500">({daysOf(v.start_date, v.end_date)} {daysOf(v.start_date, v.end_date) === 1 ? "zi" : "zile"})</span>
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>
        {v.note && <span className="text-xs text-zinc-600 italic">📝 {v.note}</span>}
        {canEdit && (
          <span className="ml-auto flex items-center gap-1">
            <button onClick={() => setEditing(v)} className="p-1.5 rounded hover:bg-zinc-100 text-zinc-600" data-testid={`vacation-edit-${v.id}`} title="Editează">
              <Pencil size={14} />
            </button>
            <button onClick={() => remove(v)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`vacation-delete-${v.id}`} title="Șterge">
              <Trash2 size={14} />
            </button>
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-5" data-testid="person-vacations-tab">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg flex items-center gap-2"><CalendarDays size={18} className="text-blue-600" /> Concedii / Absențe</h3>
        {canEdit && (
          <Button size="sm" onClick={() => setEditing({})} data-testid="vacation-add-btn">
            <Plus size={14} className="mr-1" /> Adaugă concediu / absență
          </Button>
        )}
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-emerald-700 mb-2">📅 În curs & urmează ({upcoming.length})</div>
        <div className="space-y-2">
          {upcoming.map((v) => <Row key={v.id} v={v} />)}
          {upcoming.length === 0 && <div className="text-sm text-zinc-400 italic">Niciun concediu sau absență viitoare.</div>}
        </div>
      </div>

      <div>
        <div className="text-xs font-bold uppercase tracking-wide text-zinc-500 mb-2">🕘 Istoric ({past.length})</div>
        <div className="space-y-2">
          {past.map((v) => <Row key={v.id} v={v} />)}
          {past.length === 0 && <div className="text-sm text-zinc-400 italic">Niciun concediu sau absență în istoric.</div>}
        </div>
      </div>

      {editing !== null && (
        <VacationFormDialog
          personId={personId}
          personName={personName}
          vacation={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

function VacationFormDialog({ personId, personName, vacation, onClose, onSaved }) {
  const isEdit = !!vacation;
  const [kind, setKind] = useState(vacation?.kind || "vacation");
  const [start, setStart] = useState(vacation?.start_date || "");
  const [end, setEnd] = useState(vacation?.end_date || "");
  const [note, setNote] = useState(vacation?.note || "");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!start || !end) { toast.error("Completează data de început și de sfârșit"); return; }
    if (end < start) { toast.error("Data de sfârșit nu poate fi înaintea celei de început"); return; }
    setSaving(true);
    try {
      const payload = { person_id: personId, person_name: personName, start_date: start, end_date: end, note: note.trim() || null, kind };
      if (isEdit) {
        const r = await api.put(`/vacations/${vacation.id}`, payload);
        toast.success(`Actualizat${r.data?.schedule_entries_affected ? ` · ${r.data.schedule_entries_affected} alocări din planificator ajustate` : ""}`);
      } else {
        const r = await api.post("/vacations", payload);
        toast.success(`Adăugat${r.data?.schedule_entries_affected ? ` · scos din ${r.data.schedule_entries_affected} alocări din planificator` : ""}`);
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editează" : "Adaugă"} concediu / absență · {personName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="small-label">Tip</Label>
            <div className="flex gap-2 mt-1">
              {[["vacation", "🏖 Concediu"], ["unavailable", "🚫 Absență / Indisponibil"]].map(([v, l]) => (
                <button
                  key={v}
                  onClick={() => setKind(v)}
                  className={`px-3 py-1.5 rounded-md border text-sm font-semibold transition-colors ${kind === v ? "bg-blue-600 text-white border-blue-600" : "bg-white text-zinc-700 border-zinc-300 hover:border-zinc-400"}`}
                  data-testid={`vacation-kind-${v}`}
                >
                  {l}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="small-label">De la</Label>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="vacation-start" />
            </div>
            <div>
              <Label className="small-label">Până la</Label>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="vacation-end" />
            </div>
          </div>
          {start && end && end >= start && (
            <div className="text-xs text-zinc-600">Durată: <strong>{daysOf(start, end)} {daysOf(start, end) === 1 ? "zi" : "zile"}</strong></div>
          )}
          <div>
            <Label className="small-label">Notă (opțional)</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="ex: concediu de odihnă, motiv medical…" data-testid="vacation-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Anulează</Button>
          <Button onClick={submit} disabled={saving} data-testid="vacation-save">{saving ? "Se salvează…" : "Salvează"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
